import { useEffect, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Receipt, ShoppingBasket, Target, Trophy } from 'lucide-react';
import {
  ApiError, compareLine, formatGbp, hourLabel, salesTone, salesWidgetChoice, salesWidgetOptions, salesWidgetPickKey, sortTakingsRows,
  type SalesAllBuildings, type SalesWidgetPick, type WidgetSize,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useWorkspaceId } from '@/lib/hooks';
import { Card, Menu, MenuItem, Spinner } from '@/components/ui';
import { relative } from '@/lib/format';
import { OriginLink as Link } from '@/lib/origin';
import { HourTicks, TONE_TEXT, useWidth } from './storeSales';

/**
 * Backlog #30: till sales from Odoo for the person's building. The API re-checks sales.view at that building.
 * t553u: `locationId` is a building id or 'all' (Transactions and Top sellers); omitted is my main building plus the
 * all-buildings takings rows (Today's takings, Average basket).
 */
export function useSalesToday(locationId?: string) {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: locationId ? ['sales', ws, 'today', locationId] : ['sales', ws, 'today'], queryFn: () => api.sales.today(ws!, locationId),
    enabled: Boolean(ws), refetchInterval: 60_000, retry: false, placeholderData: locationId ? keepPreviousData : undefined,
  });
}

export const noSalesBuilding = (e: unknown) => e instanceof ApiError && e.status === 404 && (e.body as { code?: string } | null)?.code === 'no_sales_building';

const readPick = (k: string | null) => { if (!k) return null; try { return localStorage.getItem(k); } catch { return null; } };
const savePick = (k: string | null, v: string | null) => {
  if (!k) return;
  try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* private mode: the choice lasts until reload */ }
};

/**
 * t553u: the building a Transactions or Top sellers widget shows, remembered per person and widget on this device
 * (as Visitors remembers its building). No choice yet means All buildings; a building I can no longer see falls back
 * to All buildings.
 */
export function useSalesWidgetBuilding(widget: SalesWidgetPick | undefined) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const key = widget && ws && userId ? salesWidgetPickKey(ws, userId, widget) : null;
  const [picked, setPicked] = useState<{ key: string | null; id: string | null }>(() => ({ key, id: readPick(key) }));
  const current = picked.key === key ? picked.id : readPick(key);
  const q = useSalesToday(widget ? salesWidgetChoice(null, current) : undefined);
  const options = q.data?.locations ? salesWidgetOptions(q.data.locations) : null;
  const choice = salesWidgetChoice(options, current);
  const pick = (id: string) => { savePick(key, id); setPicked({ key, id }); };
  // A remembered building I've lost access to: forget it and show All buildings.
  const lost = Boolean(current) && q.error instanceof ApiError && q.error.status === 403;
  useEffect(() => { if (lost) { savePick(key, null); setPicked({ key, id: null }); } }, [lost, key]);
  return { q, options: options ?? [], choice, pick };
}

function BuildingMenu({ options, choice, onPick, name }: { options: { id: string; name: string }[]; choice: string; onPick: (id: string) => void; name: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Menu open={open} setOpen={setOpen} fixed trigger={
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`Building: ${name}`} title="Choose building" data-testid="sales-building-switch"
        className="inline-flex max-w-[11rem] items-center gap-0.5 rounded text-xs text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-slate-400 dark:hover:text-slate-200">
        <span className="truncate">{name}</span><ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
    }>
      <div role="menu" aria-label="Building" className="max-h-72 overflow-y-auto">
        {options.map(o => (
          <MenuItem key={o.id} onClick={() => { setOpen(false); if (o.id !== choice) onPick(o.id); }} data-testid="sales-building-option"
            icon={o.id === choice ? <Check className="h-3.5 w-3.5 shrink-0" aria-label="Selected" /> : <span className="w-3.5 shrink-0" />}>
            <span className={o.id === choice ? 'font-medium' : undefined}>{o.name}</span>
          </MenuItem>
        ))}
      </div>
    </Menu>
  );
}

const T = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => <span className="flex items-center gap-2">{icon}{children}</span>;
const ic = 'h-4 w-4 text-accent-fg';

type Data = NonNullable<ReturnType<typeof useSalesToday>['data']>;
function Shell({ title, icon, children, multi, pick }: { title: string; icon: ReactNode; children: (d: Data) => ReactNode; multi?: (a: SalesAllBuildings, last: string | null) => ReactNode; pick?: SalesWidgetPick }) {
  const b = useSalesWidgetBuilding(pick);
  const q = b.q;
  // Feedback #6: none of my buildings has sales (e.g. warehouse-only staff) - the widget hides itself.
  if (noSalesBuilding(q.error)) return null;
  // t485u: several buildings with tills - no single building name in the header, the card lists them all.
  if (multi && q.data?.all) return <Card title={<T icon={icon}>{title}</T>}>{multi(q.data.all, q.data.last_synced_at)}</Card>;
  // t553u: two or more buildings - the building name in the header is a small menu (All buildings first).
  const switchable = Boolean(pick) && b.options.length > 1;
  const name = b.options.find(o => o.id === b.choice)?.name ?? q.data?.location_name ?? '';
  const actions = !q.data ? null : switchable ? <BuildingMenu options={b.options} choice={b.choice} onPick={b.pick} name={name} /> : <span className="text-xs text-slate-500">{q.data.location_name}</span>;
  return (
    <Card title={<T icon={icon}>{title}</T>} actions={actions}>
      {q.isLoading ? <Spinner /> : q.error ? <p className="text-sm text-slate-500">{errorMessage(q.error)}</p> : <div className={q.isPlaceholderData ? 'opacity-60' : undefined}>{children(q.data!)}</div>}
    </Card>
  );
}

const synced = (t: string | null) => (t ? `Updated ${relative(t)}` : 'Not synced yet');

export function TakingsWidget({ size }: { size: WidgetSize }) {
  return (
    <Shell title="Today's takings" icon={<Target className={ic} />} multi={(a, last) => <AllBuildingsTakings a={a} last={last} />}>
      {d => {
        const pct = d.progress != null ? Math.round(d.progress * 100) : null;
        return (
          <div className="space-y-3" data-testid="sales-takings">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-3xl font-semibold tabular-nums">{formatGbp(d.takings_pence)}</span>
              {d.target_pence != null && <span className="text-sm text-slate-500">of {formatGbp(d.target_pence)} target</span>}
            </div>
            {pct != null ? (
              <div>
                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Progress to today's target">
                  <div className="h-full rounded-full bg-brand-yellow" style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  {pct >= 100 ? <span className="font-medium">Target hit · {pct}%</span> : `${pct}% · ${formatGbp(Math.max(0, d.target_pence! - d.takings_pence))} to go`}
                </p>
              </div>
            ) : <p className="text-xs text-slate-500">No daily target set for this building.</p>}
            {size !== 's' && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Period label="This week" v={d.week} />
                <Period label="This month" v={d.month} />
              </div>
            )}
            <p className="text-xs text-slate-500">{synced(d.last_synced_at)}</p>
          </div>
        );
      }}
    </Shell>
  );
}

/** "62% of £400 target", "Target hit · 112% of £400", or nothing when no daily target is set for today. */
export function targetText(progress: number | null, target: number | null) {
  if (progress == null || !target) return null;
  const pct = Math.round(progress * 100);
  return pct >= 100 ? `Target hit · ${pct}% of ${formatGbp(target)}` : `${pct}% of ${formatGbp(target)} target`;
}

/**
 * t485u: Today's takings across every building I can see sales at (2+ with a till): the total with ATV and the
 * comparison with this time yesterday, then one row per building, highest takings first (sortTakingsRows; no sales yet go last, by name). A row opens the building's
 * Today's sales panel. Long lists scroll inside the card.
 */
export function AllBuildingsTakings({ a, last }: { a: SalesAllBuildings; last: string | null }) {
  const t = a.total;
  const tone = salesTone(t.revenue_pence, a.yesterday_same_time.revenue_pence);
  return (
    <div className="space-y-3" data-testid="sales-takings-all">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className={`text-3xl font-semibold tabular-nums ${TONE_TEXT[tone]}`} data-tone={tone}>{formatGbp(t.revenue_pence)}</span>
          <span className="text-sm text-slate-500 tabular-nums">{t.orders} order{t.orders === 1 ? '' : 's'} · ATV {t.atv_pence == null ? '-' : formatGbp(t.atv_pence)}</span>
        </div>
        <p className="text-xs text-slate-500"><span className={TONE_TEXT[tone]} data-testid="sales-takings-compare">{compareLine(t.revenue_pence, a.yesterday_same_time.revenue_pence)}</span> · {a.buildings.length} buildings</p>
      </div>
      <ul className="scroll-thin -mx-2 max-h-[26rem] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800" aria-label="Takings by building">
        {sortTakingsRows(a.buildings).map(b => {
          const tt = targetText(b.progress, b.target_pence);
          return (
            <li key={b.location_id}>
              <Link to={`/buildings/${b.location_id}#todays-sales`} className="block rounded-md px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/60" data-testid="sales-building-row">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{b.location_name}</span>
                  <span className="text-sm font-semibold tabular-nums">{formatGbp(b.today.revenue_pence)}</span>
                </div>
                <div className="text-xs text-slate-500 tabular-nums">
                  {b.today.orders === 0 && !b.today.revenue_pence ? `No sales yet today${tt ? ` · ${tt}` : ''}` : <>{b.today.orders} order{b.today.orders === 1 ? '' : 's'} · ATV {b.today.atv_pence == null ? '-' : formatGbp(b.today.atv_pence)}{tt ? ` · ${tt}` : ''}</>}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-slate-500">{synced(a.as_of ?? last)}</p>
    </div>
  );
}

function Period({ label, v }: { label: string; v: { takings_pence: number; target_pence: number | null } }) {
  const pct = v.target_pence ? Math.round((v.takings_pence / v.target_pence) * 100) : null;
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-900">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-semibold tabular-nums">{formatGbp(v.takings_pence)}</div>
      {pct != null && <div className="text-xs text-slate-500">{pct}% of {formatGbp(v.target_pence)}</div>}
    </div>
  );
}

export function TransactionsWidget({ size }: { size: WidgetSize }) {
  return (
    <Shell title="Transactions" icon={<Receipt className={ic} />} pick="sales_transactions">
      {d => <TransactionsBody d={d} size={size} />}
    </Shell>
  );
}

/** t568u: every bar labelled underneath with its start hour ("13:00", "13" when narrow), matching its tooltip. */
function TransactionsBody({ d, size }: { d: Data; size: WidgetSize }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const max = Math.max(1, ...d.by_hour.map(h => h.transactions));
  const gap = size === 'm' ? 4 : 2;
  return (
    <div className="space-y-2" data-testid="sales-transactions">
      <div className="flex items-baseline gap-2"><span className="text-3xl font-semibold tabular-nums">{d.transactions}</span><span className="text-sm text-slate-500">today{d.refunds ? ` · ${d.refunds} refund${d.refunds === 1 ? '' : 's'}` : ''}</span></div>
      {d.by_hour.length > 0 && (
        <div ref={ref} aria-label="Transactions by hour">
          <div className={size === 'm' ? 'flex h-20 items-end' : 'flex h-12 items-end'} style={{ gap }}>
            {d.by_hour.map(h => (
              <div key={h.hour} className="flex h-full flex-1 items-end" data-hour={h.hour} title={`${hourLabel(h.hour)} · ${h.transactions} transactions · ${formatGbp(h.takings_pence)}`}>
                <div className="w-full rounded-t bg-brand-yellow ring-1 ring-inset ring-ink/20" style={{ height: `${Math.max(4, (h.transactions / max) * 100)}%` }} />
              </div>
            ))}
          </div>
          <HourTicks hours={d.by_hour.map(h => h.hour)} width={width} gap={gap} className="text-[10px]" />
        </div>
      )}
    </div>
  );
}

export function BasketWidget() {
  return (
    <Shell title="Average basket" icon={<ShoppingBasket className={ic} />}>
      {d => (
        <div data-testid="sales-basket">
          <div className="text-3xl font-semibold tabular-nums">{formatGbp(d.average_basket_pence)}</div>
          <p className="text-xs text-slate-500">{d.transactions} transactions today</p>
        </div>
      )}
    </Shell>
  );
}

export function TopSellersWidget({ size }: { size: WidgetSize }) {
  return (
    <Shell title="Top sellers" icon={<Trophy className={ic} />} pick="sales_top">
      {d => !d.top_sellers.length ? <p className="text-sm text-slate-500">No sales yet today.</p> : (
        <ol className="space-y-1.5 text-sm" data-testid="sales-top">
          {d.top_sellers.slice(0, size === 'm' ? 5 : 3).map((p, i) => (
            <li key={p.product_ref} className="flex items-center gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-xs font-semibold text-on-accent">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate">{p.product_name}</span>
              <span className="tabular-nums text-slate-500">{p.qty} · {formatGbp(p.takings_pence)}</span>
            </li>
          ))}
        </ol>
      )}
    </Shell>
  );
}

